// Exemplos da API pública de SMS (/api/sms/*) mostrados na tela /messages-api.
//
// Módulo puro de propósito: o spec __tests__/smsExamples.test.js valida cada
// snippet mecanicamente (JSON.parse, @babel/parser, php -l, compile() do
// Python) sem montar React. Mudou o contrato de /api/sms/send? Atualize aqui
// TODAS as linguagens com o valor completo — nunca só um comentário.
//
// `lines` são as descrições de campo (i18n, resolvidas na chamada, no idioma
// atual); `copy` e `examples` são código, iguais em qualquer idioma.
import { i18n } from "../../translate/i18n";

const TOKEN = "SEU_TOKEN_SMS";
const ORIGEM = "ERP Financeiro";

export const buildSmsExamples = (baseUrl = "") => {
  const sendUrl = `${baseUrl}/api/sms/send`;
  const statusUrl = `${baseUrl}/api/sms/123`;
  const balanceUrl = `${baseUrl}/api/sms/balance`;

  const send = {
    method: "POST",
    url: sendUrl,
    lines: [
      i18n.t("messagesAPIInstructions.smsFieldNumber"),
      i18n.t("messagesAPIInstructions.smsFieldBody"),
      i18n.t("messagesAPIInstructions.smsFieldLink"),
      i18n.t("messagesAPIInstructions.smsFieldLinkUrl"),
      i18n.t("messagesAPIInstructions.smsFieldLinkWhatsapp"),
      i18n.t("messagesAPIInstructions.smsFieldLinkPrefilled"),
    ],
    response: i18n.t("messagesAPIInstructions.smsResponseSend"),
    copy: [
      `POST ${sendUrl}`,
      `Authorization: Bearer ${TOKEN}`,
      `X-Origin: ${ORIGEM}`,
      "Content-Type: application/json",
      "",
      "{",
      '  "number": "5531999999999",',
      '  "body": "Seu boleto vence amanhã. Pague em {link}",',
      '  "link": { "url": "https://exemplo.com/boleto/123" }',
      "}",
    ].join("\n"),
    examples: {
      curl: `curl -X POST "${sendUrl}" \\
  -H "Authorization: Bearer ${TOKEN}" \\
  -H "X-Origin: ${ORIGEM}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "5531999999999",
    "body": "Seu boleto vence amanhã. Pague em {link}",
    "link": { "url": "https://exemplo.com/boleto/123" }
  }'
# link: opcional; exige {link} no body (e vice-versa). Use url (http/https)
# OU whatsappNumber (só dígitos) + prefilledText opcional, nunca os dois.
# 200 => { "id": 123, "status": "sent", "providerMessageId": "...", "parts": 1, "balance": 998 }`,
      node: `const axios = require("axios");

const { data } = await axios.post(
  "${sendUrl}",
  {
    number: "5531999999999",
    body: "Seu boleto vence amanhã. Pague em {link}",
    // Opcional; exige {link} no body. Alternativa ao url:
    // { whatsappNumber: "5531988887777", prefilledText: "Quero pagar" }
    link: { url: "https://exemplo.com/boleto/123" },
  },
  { headers: { Authorization: "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}" } }
);
// data => { id, status: "sent", providerMessageId, parts, balance }`,
      ts: `const resposta = await fetch("${sendUrl}", {
  method: "POST",
  headers: {
    Authorization: "Bearer ${TOKEN}",
    "X-Origin": "${ORIGEM}",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    number: "5531999999999",
    body: "Seu boleto vence amanhã. Pague em {link}",
    // Opcional; exige {link} no body. Alternativa ao url:
    // { whatsappNumber: "5531988887777", prefilledText: "Quero pagar" }
    link: { url: "https://exemplo.com/boleto/123" },
  }),
});

const dados: {
  id: number;
  status: "sent";
  providerMessageId: string;
  parts: number;
  balance: number | null;
  debitPending?: boolean;
} = await resposta.json();`,
      php: `<?php
$ch = curl_init("${sendUrl}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer ${TOKEN}",
    "X-Origin: ${ORIGEM}",
    "Content-Type: application/json",
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    "number" => "5531999999999",
    "body"   => "Seu boleto vence amanhã. Pague em {link}",
    // Opcional; exige {link} no body. Alternativa ao url:
    // ["whatsappNumber" => "5531988887777", "prefilledText" => "Quero pagar"]
    "link"   => ["url" => "https://exemplo.com/boleto/123"],
]));
$resposta = json_decode(curl_exec($ch), true);
curl_close($ch);`,
      python: `import requests

resposta = requests.post(
    "${sendUrl}",
    json={
        "number": "5531999999999",
        "body": "Seu boleto vence amanhã. Pague em {link}",
        # Opcional; exige {link} no body. Alternativa ao url:
        # {"whatsappNumber": "5531988887777", "prefilledText": "Quero pagar"}
        "link": {"url": "https://exemplo.com/boleto/123"},
    },
    headers={"Authorization": "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}"},
)
dados = resposta.json()`,
    },
  };

  const status = {
    method: "GET",
    url: `${baseUrl}/api/sms/:id`,
    lines: [],
    response: i18n.t("messagesAPIInstructions.smsResponseStatus"),
    copy: [
      `GET ${statusUrl}`,
      `Authorization: Bearer ${TOKEN}`,
      `X-Origin: ${ORIGEM}`,
    ].join("\n"),
    examples: {
      curl: `curl "${statusUrl}" \\
  -H "Authorization: Bearer ${TOKEN}" \\
  -H "X-Origin: ${ORIGEM}"
# 200 => { "id": 123, "number": "5531999999999", "status": "delivered", ... }
# 404 => { "error": "SMS não encontrado." }`,
      node: `const axios = require("axios");

const { data } = await axios.get("${statusUrl}", {
  headers: { Authorization: "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}" },
});
// data.status: "pending" | "sent" | "delivered" | "failed"`,
      ts: `const resposta = await fetch("${statusUrl}", {
  headers: { Authorization: "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}" },
});

const sms: {
  id: number;
  number: string;
  status: "pending" | "sent" | "delivered" | "failed";
  failureReason: string | null;
  origin: string;
  parts: number;
  sentAt: string | null;
  deliveredAt: string | null;
} = await resposta.json();`,
      php: `<?php
$ch = curl_init("${statusUrl}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer ${TOKEN}",
    "X-Origin: ${ORIGEM}",
]);
$sms = json_decode(curl_exec($ch), true);
curl_close($ch);
// $sms["status"]: pending | sent | delivered | failed`,
      python: `import requests

resposta = requests.get(
    "${statusUrl}",
    headers={"Authorization": "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}"},
)
sms = resposta.json()  # sms["status"]: pending | sent | delivered | failed`,
    },
  };

  const balance = {
    method: "GET",
    url: balanceUrl,
    lines: [],
    response: i18n.t("messagesAPIInstructions.smsResponseBalance"),
    copy: [
      `GET ${balanceUrl}`,
      `Authorization: Bearer ${TOKEN}`,
      `X-Origin: ${ORIGEM}`,
    ].join("\n"),
    examples: {
      curl: `curl "${balanceUrl}" \\
  -H "Authorization: Bearer ${TOKEN}" \\
  -H "X-Origin: ${ORIGEM}"
# 200 => { "balance": 998 }`,
      node: `const axios = require("axios");

const { data } = await axios.get("${balanceUrl}", {
  headers: { Authorization: "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}" },
});
// data.balance => créditos (partes de SMS) disponíveis`,
      ts: `const resposta = await fetch("${balanceUrl}", {
  headers: { Authorization: "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}" },
});

const { balance }: { balance: number } = await resposta.json();`,
      php: `<?php
$ch = curl_init("${balanceUrl}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer ${TOKEN}",
    "X-Origin: ${ORIGEM}",
]);
$saldo = json_decode(curl_exec($ch), true)["balance"];
curl_close($ch);`,
      python: `import requests

resposta = requests.get(
    "${balanceUrl}",
    headers={"Authorization": "Bearer ${TOKEN}", "X-Origin": "${ORIGEM}"},
)
saldo = resposta.json()["balance"]`,
    },
  };

  return { send, status, balance };
};

export default buildSmsExamples;
